import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import moment from "moment";
import { Button } from "react-bootstrap";

import {
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  Tooltip,
} from "recharts";

import { Bar } from "react-chartjs-2";

import {
  isNilOrEmpty,
  addDays,
  formatNumber,
  convertDateWithShotMonthName,
  convertDate,
  isNotNil,
} from "src/main_app/utils/common";

import { PageHeading } from "../../components";
import AlertsFilter from "../../components/alerts_filter/alerts-filter";
import {
  getAlertsOverview,
  getCustomersDetail,
  getSixCustomerTheMostAlert,
} from "src/main_app/services/apis";

import {
  isEmpty,
  isNil,
  propOr,
  compose,
  find,
  propEq,
  reduce,
  map,
  last,
} from "ramda";
import { getApiUrl } from "../../utils/config-utility";
import { authService } from "../../../base/services";
import AlertsPageSkeleton from "./alerts-page-skeleton";
import AlertCustomerRealTimeUsagePage from "./alert-customers-real-time-usage";
import { getCustomerWaterUsage } from "src/main_app/services/apis";
import alertTypes from "./helpers/alert-types";
import { getTenantInfoFromLocal } from "src/main_app/services/local_storage/tenant-info";

const limit = 20;

const ticksFormatter = (value, index) => moment(value).format("MMM DD");

const yLabel = {
  value: "Count",
  angle: -90,
  position: "top",
  dy: 170,
  dx: 0,
};

import AlertList from "./alert-list";
import getSearchData from "./helpers/get-search-data";
import {
  useTenantConfig,
  useUserPermissions,
  checkPermission,
} from "src/main_app/actions";
import AlertsPageChartRealTimeSkeleton from "./alerts-page-chart-realtime-skeleton";

const tooltipContentStyle = {
  boxShadow: "0 10px 24px rgba(29,42,68,0.12)",
  borderRadius: "4px",
  border: "none",
  textAlign: "center",
};

const tooltipItemStyle = {
  color: "#05d6f",
  fontSize: "12px",
  fontWeight: "500",
};

const renderTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div
      className="custom-line-tooltip"
      style={{
        padding: "5px",
        backgroundColor: "#fff",
        boxShadow: "0 0 8px 8px rgb(0 0 0 / 7%)",
        borderRadius: "4px",
      }}
    >
      <h5 className="mb-0">{payload[0].payload.displayDate}</h5>
      <br />
      {payload.map((payloadItem, index) => (
        <div
          key={index + ""}
          style={{
            color: payloadItem.stroke,
          }}
        >
          {payloadItem.name}: {formatNumber(payloadItem.value)}{" "}
        </div>
      ))}
    </div>
  );
};

const AlertsPage = ({ onTabCustomerID = null }) => {
  const tenantInfo = useRef(getTenantInfoFromLocal());

  const tenantConfig = useTenantConfig();

  const allowAlertTypes = useMemo(
    () => propOr([], "allowAlertTypes", tenantConfig),
    [tenantConfig]
  );

  const [isLoaded, setIsLoaded] = useState(false);
  const filterRef = useRef(null);
  const alertListRef = useRef(null);

  const [total, setTotal] = useState({
    all: 0,
  });
  const [totalAll, setTotalAll] = useState({
    all: 0,
  });

  const [dataOverview, setDataOverview] = useState([]);
  const [alertsChartData, setAlertsChartData] = useState([]);
  const [dataBarchart, setDataBarChart] = useState([]);
  const [ticks, setTicks] = useState([]);
  const [pressureAlertDetails, setPressureAlertDetails] = useState([]);
  const [selectedPressureAlertSites, setSelectedPressureAlertSites] = useState([]);
  const [selectedPressureAlertDate, setSelectedPressureAlertDate] = useState("");
  const [selectedPressureAlertSeverity, setSelectedPressureAlertSeverity] =
    useState("");

  const userPermissions = useUserPermissions();
  const canViewThisPage = useMemo(
    () => checkPermission(userPermissions, "alerts-page", "canView"),
    [userPermissions]
  );
  useEffect(() => {
    if (!canViewThisPage) {
      alert("You don't have permission to view this page");
      document.location.href = "/";
    }
  }, [canViewThisPage]);

  // const [aspect, setAspect] = useState(getAspect(window.innerWidth));

  // useEffect(() => {
  //   const handleResize = () => setAspect(getAspect(window.innerWidth));
  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, []);

  // function getAspect(width) {
  //   if (width < 480) return 10 / 7; // taller for mobile
  //   if (width < 768) return 10 / 5; // tablet
  //   return 10 / 3; // desktop
  // }

  const getBaseExportUrl = () =>
    `${getApiUrl()}/export/alert/csv?&token=${authService.getAccessToken()}${
      onTabCustomerID ? `search[customer_id]=${onTabCustomerID}` : ""
    }`;

  const [exportUrl, setExportUrl] = useState(getBaseExportUrl());
  const hasAny = useRef(false);

  const updateExportUrl = (searchData) => {
    let newExportUrl = getBaseExportUrl();
    Object.keys(searchData).forEach((filter) => {
      if (searchData[filter]) {
        newExportUrl += `&${filter}=${searchData[filter]}`;
      }
    });
    setExportUrl(newExportUrl);
  };

  const loadAlertsOverview = async (searchData) => {
    const { data: result } = await getAlertsOverview(searchData);
    setDataOverview(result.overviewBySeverity);
    setPressureAlertDetails(propOr([], "data_detail", result));
    setSelectedPressureAlertSites([]);
    setSelectedPressureAlertDate("");
    setSelectedPressureAlertSeverity("");

    hasAny.current =
      reduce(
        (sumItems, item) => {
          return sumItems + (item.count - "");
        },
        0,
        result.overviewBySeverity
      ) > 0;

    const nTotal = {
      all: reduce(
        (sumItems, item) => {
          return sumItems + (item.count - "");
        },
        0,
        result.overviewByType
      ),
    };
    for (const alertType of alertTypes) {
      nTotal[alertType.name] =
        (
          result.overviewByType.find((i) => i.type === alertType.name) ?? {
            count: 0,
          }
        ).count - "";
    }

    const nTotalAll = {
      all: reduce(
        (sumItems, item) => {
          return sumItems + (item.count - "");
        },
        0,
        result.overviewByTypeAll
      ),
    };
    for (const alertType of alertTypes) {
      nTotalAll[alertType.name] =
        (
          result.overviewByTypeAll.find((i) => i.type === alertType.name) ?? {
            count: 0,
          }
        ).count - "";
    }

    setTotal(nTotal);
    setTotalAll(nTotalAll);

    const { from, to } = searchData;

    const formDate = new Date(from);
    const toDate = new Date(to);

    const combinedData = [];
    const nTicks = [];
    for (
      let currentDate = formDate;
      currentDate <= toDate;
      currentDate = addDays(currentDate, 1)
    ) {
      const currentDateString = moment(currentDate).format(
        "yyyy-MM-DD 00:00:00"
      );
      const dataAtTheDate = {
        date: currentDate,
        daeString: currentDateString,
      };
      for (const alertType of alertTypes) {
        if (
          !allowAlertTypes.some(
            (allowAlertType) => allowAlertType === alertType.name
          )
        )
          continue;
        const alertsByType = result.data.find(
          (i) => i.type === alertType.name && i.created_at === currentDateString
        );
        if (alertsByType) {
          dataAtTheDate[alertType.name] = alertsByType.count - 0;
          
          // Special handling for pressure_flow - split by severity
          if (alertType.name === 'pressure_flow') {
            const pressureAlerts = result.data.filter(
              (i) => i.type === 'pressure_flow' && i.created_at === currentDateString
            );
            
            let highCount = 0, low1Count = 0, low2Count = 0;
            
            pressureAlerts.forEach(alert => {
              if (alert.severity === 'high') {
                highCount += alert.count - 0;
              } else if (alert.severity === 'low_1') {
                low1Count += alert.count - 0;
              } else if (alert.severity === 'low_2') {
                low2Count += alert.count - 0;
              }
            });
            
            dataAtTheDate['pressure_flow_high'] = highCount;
            dataAtTheDate['pressure_flow_low_1'] = low1Count;
            dataAtTheDate['pressure_flow_low_2'] = low2Count;
          }
        } else {
          dataAtTheDate[alertType.name] = 0;
          
          // Initialize pressure_flow severity counts to 0
          if (alertType.name === 'pressure_flow') {
            dataAtTheDate['pressure_flow_high'] = 0;
            dataAtTheDate['pressure_flow_low_1'] = 0;
            dataAtTheDate['pressure_flow_low_2'] = 0;
          }
        }
      }
      combinedData.push(dataAtTheDate);
      nTicks.push(currentDate);
    }

    setAlertsChartData(combinedData);
    setTicks(nTicks);

    const arr_date = [];
    const arr_data_loss = [];
    const arr_night_flow = [];
    const arr_peak_flow = [];
    const arr_step_change = [];
    const arr_water_quality = [];
    const arr_base_flow = [];
    const arr_leak_flow = [];
    const arr_pressure_flow = [];
    const arr_pressure_flow_high = [];
    const arr_pressure_flow_low_1 = [];
    const arr_pressure_flow_low_2 = [];
    const arr_low_temperature = [];
    const arr_high_temperature = [];
    const arr_low_battery = [];
    const arr_empty_pipe = [];

    combinedData.forEach((item) => {
      arr_date.push(convertDateWithShotMonthName(item["date"]));
      arr_data_loss.push(item["data_loss"]);
      arr_night_flow.push(item["night_flow"]);
      arr_peak_flow.push(item["peak_flow"]);
      arr_step_change.push(item["step_change"]);
      arr_water_quality.push(item["water_quality"]);
      arr_base_flow.push(item["base_flow"]);
      arr_leak_flow.push(item["leak_flow"]);
      arr_pressure_flow.push(item["pressure_flow"]);
      arr_pressure_flow_high.push(item["pressure_flow_high"] || 0);
      arr_pressure_flow_low_1.push(item["pressure_flow_low_1"] || 0);
      arr_pressure_flow_low_2.push(item["pressure_flow_low_2"] || 0);
      arr_low_temperature.push(item["low_temperature"]);
      arr_high_temperature.push(item["high_temperature"]);
      arr_low_battery.push(item["low_battery"]);
      arr_empty_pipe.push(item["empty_pipe"]);
    });

    const dataSet = [];

    allowAlertTypes.forEach((item) => {
      switch (item) {
        case "night_flow":
          dataSet.push({
            label: "Night flow",
            // backgroundColor: 'rgb(194,53,49)',
            backgroundColor: "#54513f",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#54513fb5",
            data: arr_night_flow,
          });
          break;
        case "data_loss":
          dataSet.push({
            label: "Data loss",
            // backgroundColor: 'rgb(47,69,84)',
            backgroundColor: "#d6a189",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#d6a1899c",
            data: arr_data_loss,
          });
          break;
        case "peak_flow":
          dataSet.push({
            label: "Peak flow",
            backgroundColor: "#97c5cc",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#97c5ccbd",
            data: arr_peak_flow,
          });
          break;
        case "water_quality":
          dataSet.push({
            label: "Water quality",
            backgroundColor: "#dca727",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#dca72799",
            data: arr_water_quality,
          });
          break;
        case "step_change":
          dataSet.push({
            label: "Step change",
            backgroundColor: "#307981",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#3079819e",
            data: arr_step_change,
          });
          break;
        case "base_flow":
          dataSet.push({
            label: "Base flow",
            backgroundColor: "#48b49b",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#48b49ba3",
            data: arr_base_flow,
          });
          break;
        case "leak_flow":
          dataSet.push({
            label: "Leak alert",
            backgroundColor: "#7a77fc",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#7a77fca3",
            data: arr_leak_flow,
          });
          break;
        case "pressure_flow":
          // Split pressure_flow into 3 separate datasets by severity
          dataSet.push({
            label: "High Pressure",
            backgroundColor: "#DC2626", // Red for high severity
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#DC26269e",
            data: arr_pressure_flow_high,
          });
          dataSet.push({
            label: "Low 1 Pressure",
            backgroundColor: "#F59E0B", // Orange for low_1 severity
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#F59E0B9e",
            data: arr_pressure_flow_low_1,
          });
          dataSet.push({
            label: "Low 2 Pressure",
            backgroundColor: "#FDE047", // Yellow for low_2 severity
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#FDE0479e",
            data: arr_pressure_flow_low_2,
          });
          break;

        case "low_temperature":
          dataSet.push({
            label: "Low temperature",
            backgroundColor: "#fffe1c",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#fffe1c9c",
            data: arr_low_temperature,
          });
          break;
        case "high_temperature":
          dataSet.push({
            label: "High temperature",
            backgroundColor: "#e77457",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#e774579c",
            data: arr_high_temperature,
          });
          break;
        case "low_battery":
          dataSet.push({
            label: "Low battery",
            backgroundColor: "#5cb85c",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#5cb85c9c",
            data: arr_low_battery,
          });
          break;
        case "empty_pipe":
          dataSet.push({
            label: "Empty pipe",
            backgroundColor: "#68223f",
            borderWidth: 1,
            stack: 1,
            hoverBackgroundColor: "#68223f9c",
            data: arr_empty_pipe,
          });
          break;
        default:
        // code block
      }
    });

    //Maping data linechart to barchart
    const dataMapingBarchart = {
      labels: arr_date,
      datasets: dataSet,
    };

    setDataBarChart(dataMapingBarchart);
  };

  const loadData = async (filteredData) => {
    const searchData = getSearchData(onTabCustomerID)(filteredData);

    //
    await loadAlertsOverview(searchData);
    alertListRef.current.setFilteredData(filteredData);

    setIsLoaded(true);

    //
    if (isEmpty(searchData)) return;
    updateExportUrl(searchData);
  };

  const displayDataOverview = useCallback(
    (severity) =>
      compose(
        formatNumber,
        propOr(0, "count"),
        find(propEq("severity", severity))
      ),
    []
  );

  const options = {
    responsive: true,
    legend: {
      display: false,
    },
    type: "bar",
    onClick: (_event, elements) => {
      if (isNilOrEmpty(elements)) {
        setSelectedPressureAlertSites([]);
        setSelectedPressureAlertDate("");
        setSelectedPressureAlertSeverity("");
        return;
      }

      const barElement = elements[0];
      const dataIndex = isNotNil(barElement?.index)
        ? barElement.index
        : barElement?._index;
      const datasetIndex = isNotNil(barElement?.datasetIndex)
        ? barElement.datasetIndex
        : barElement?._datasetIndex;

      if (isNil(dataIndex) || isNil(datasetIndex)) return;

      const chartDate = alertsChartData?.[dataIndex]?.daeString;
      const chartDisplayDate = dataBarchart?.labels?.[dataIndex] || "";
      const datasetLabel = dataBarchart?.datasets?.[datasetIndex]?.label || "";
      const isPressureDataset = datasetLabel.toLowerCase().includes("pressure");

      if (!chartDate || !isPressureDataset) {
        setSelectedPressureAlertSites([]);
        setSelectedPressureAlertDate("");
        setSelectedPressureAlertSeverity("");
        return;
      }

      let severityFilter = "";
      if (datasetLabel === "High Pressure") severityFilter = "high";
      if (datasetLabel === "Low 1 Pressure") severityFilter = "low_1";
      if (datasetLabel === "Low 2 Pressure") severityFilter = "low_2";

      const matchedSites = pressureAlertDetails.filter((item) => {
        if (item.type !== "pressure_flow") return false;
        if (item.created_at !== chartDate) return false;
        if (severityFilter && item.severity !== severityFilter) return false;
        return true;
      });

      const uniqueSites = Array.from(
        new Map(
          matchedSites.map((item) => [
            `${item.uuid || ""}|${item.meter_id || ""}|${item.address || ""}`,
            item,
          ])
        ).values()
      );

      setSelectedPressureAlertSites(uniqueSites);
      setSelectedPressureAlertDate(chartDisplayDate);
      setSelectedPressureAlertSeverity(severityFilter || "all");
    },
  };

  /////////////////////Chart Real time data (Sums data)//////////////////////////
  const ticksRef = useRef([]);
  const [isNightFlowAlert, setIsNightFlowAlert] = useState(false);
  const [isLoadedRealTimeChart, setIsLoadRealTimeChart] = useState(true);
  const [nameCustomer, setNameCustomer1] = useState(null);
  const [nameCustomer2, setNameCustomer2] = useState(null);
  const [nameCustomer3, setNameCustomer3] = useState(null);
  const [nameCustomer4, setNameCustomer4] = useState(null);
  const [nameCustomer5, setNameCustomer5] = useState(null);
  const [nameCustomer6, setNameCustomer6] = useState(null);
  const [nameCustomer7, setNameCustomer7] = useState(null);
  const [nameCustomer8, setNameCustomer8] = useState(null);
  const [nameCustomer9, setNameCustomer9] = useState(null);

  const [customer, setCustomer1] = useState(null);
  const [customer2, setCustomer2] = useState(null);
  const [customer3, setCustomer3] = useState(null);
  const [customer4, setCustomer4] = useState(null);
  const [customer5, setCustomer5] = useState(null);
  const [customer6, setCustomer6] = useState(null);
  const [customer7, setCustomer7] = useState(null);
  const [customer8, setCustomer8] = useState(null);
  const [customer9, setCustomer9] = useState(null);

  const [dataWeekly, setDataWeekly1] = useState(null);
  const [meterNames, setMeterNames1] = useState([]);

  const [dataWeekly2, setDataWeekly2] = useState(null);
  const [meterNames2, setMeterNames2] = useState([]);

  const [dataWeekly3, setDataWeekly3] = useState(null);
  const [meterNames3, setMeterNames3] = useState([]);

  const [dataWeekly4, setDataWeekly4] = useState(null);
  const [meterNames4, setMeterNames4] = useState([]);

  const [dataWeekly5, setDataWeekly5] = useState(null);
  const [meterNames5, setMeterNames5] = useState([]);

  const [dataWeekly6, setDataWeekly6] = useState(null);
  const [meterNames6, setMeterNames6] = useState([]);

  const [dataWeekly7, setDataWeekly7] = useState(null);
  const [meterNames7, setMeterNames7] = useState([]);

  const [dataWeekly8, setDataWeekly8] = useState(null);
  const [meterNames8, setMeterNames8] = useState([]);

  const [dataWeekly9, setDataWeekly9] = useState(null);
  const [meterNames9, setMeterNames9] = useState([]);

  const getDataWeekly = async (type) => {
    setNameCustomer1(null);
    setNameCustomer2(null);
    setNameCustomer3(null);
    setNameCustomer4(null);
    setNameCustomer5(null);
    setNameCustomer6(null);
    setNameCustomer7(null);
    setNameCustomer8(null);
    setNameCustomer9(null);

    setDataWeekly1(null);
    setMeterNames1([]);
    setDataWeekly2(null);
    setMeterNames2([]);

    setDataWeekly3(null);
    setMeterNames3([]);
    setDataWeekly4(null);
    setMeterNames4([]);

    setDataWeekly5(null);
    setMeterNames5([]);
    setDataWeekly6(null);
    setMeterNames6([]);

    setDataWeekly7(null);
    setMeterNames7([]);
    setDataWeekly8(null);
    setMeterNames8([]);
    setDataWeekly9(null);
    setMeterNames9([]);

    const res = await getSixCustomerTheMostAlert(type);
    const customer_ids = propOr([], "data", res);
    if (!isEmpty(customer_ids)) {
      for (let i = 0; i < customer_ids.length; i++) {
        const item = customer_ids[i];
        const dataRealTime = await mapDataSums(item["customer_id"]);
        const { data: data } = await getCustomersDetail(item["customer_id"]);
        const customerObj = data.data;
        const nameOfCustomer = propOr(null, "name", data.data);
        switch (i) {
          case 0:
            setDataWeekly1(dataRealTime["arrDataWeekly"]);
            setMeterNames1(dataRealTime["setOfMeterNames"]);
            setNameCustomer1(nameOfCustomer);
            setCustomer1(customerObj);
            break;
          case 1:
            setDataWeekly2(dataRealTime["arrDataWeekly"]);
            setMeterNames2(dataRealTime["setOfMeterNames"]);
            setNameCustomer2(nameOfCustomer);
            setCustomer2(customerObj);
            break;
          case 2:
            setDataWeekly3(dataRealTime["arrDataWeekly"]);
            setMeterNames3(dataRealTime["setOfMeterNames"]);
            setNameCustomer3(nameOfCustomer);
            setCustomer3(customerObj);
            break;
          case 3:
            setDataWeekly4(dataRealTime["arrDataWeekly"]);
            setMeterNames4(dataRealTime["setOfMeterNames"]);
            setNameCustomer4(nameOfCustomer);
            setCustomer4(customerObj);
            break;
          case 4:
            setDataWeekly5(dataRealTime["arrDataWeekly"]);
            setMeterNames5(dataRealTime["setOfMeterNames"]);
            setNameCustomer5(nameOfCustomer);
            setCustomer5(customerObj);
            break;
          case 5:
            setDataWeekly6(dataRealTime["arrDataWeekly"]);
            setMeterNames6(dataRealTime["setOfMeterNames"]);
            setNameCustomer6(nameOfCustomer);
            setCustomer6(customerObj);
            break;
          case 6:
            setDataWeekly7(dataRealTime["arrDataWeekly"]);
            setMeterNames7(dataRealTime["setOfMeterNames"]);
            setNameCustomer7(nameOfCustomer);
            setCustomer7(customerObj);
            break;
          case 7:
            setDataWeekly8(dataRealTime["arrDataWeekly"]);
            setMeterNames8(dataRealTime["setOfMeterNames"]);
            setNameCustomer8(nameOfCustomer);
            setCustomer8(customerObj);
            break;
          case 8:
            setDataWeekly9(dataRealTime["arrDataWeekly"]);
            setMeterNames9(dataRealTime["setOfMeterNames"]);
            setNameCustomer9(nameOfCustomer);
            setCustomer9(customerObj);
            break;
        }
      }
    }
  };

  const mapDataSums = async (customer_id) => {
    const ticks = [];
    const today = new Date();
    const to = addDays(today, -1);
    const from = addDays(today, -3);
    const {
      data: { data },
    } = await getCustomerWaterUsage(customer_id, {
      from: convertDate(from),
      to: convertDate(to),
    });

    const allMeterNames = [];

    let arrDataWeekly = map((item) => {
      const { time, ...metersData } = item;

      if (isEmpty(metersData)) return { time };

      const utcTime = moment(time).utcOffset(0, true);

      const utcUnix = utcTime.unix();

      if (isEmpty(ticks) || utcUnix - last(ticks) >= 86400) {
        ticks.push(utcUnix);
      }

      const combinedData = {
        date: utcUnix,
        displayDate: utcTime.format("ddd MMM DD hh:mm a"),
      };

      for (const meterName in metersData) {
        if (!Object.hasOwnProperty.call(metersData, meterName)) continue;
        allMeterNames.push(meterName);
        const sumUsage = metersData[meterName];
        combinedData[meterName] = sumUsage
          ? Math.round(sumUsage * 100) / 100
          : null;
      }

      ticksRef.current = ticks;

      return combinedData;
    }, data);

    const setOfMeterNames = [...new Set(allMeterNames)];
    arrDataWeekly = map((item) => {
      for (const meterName of setOfMeterNames) {
        if (isNotNil(item[meterName])) continue;
        item[meterName] = 0;
      }

      return item;
    }, arrDataWeekly);

    return { setOfMeterNames, arrDataWeekly };
  };

  const nightFlowAlert = async (type) => {
    if (type === "night_flow" || type === "base_flow") {
      setIsLoadRealTimeChart(false);
      await getDataWeekly(type);
      setIsNightFlowAlert(true);
      setIsLoadRealTimeChart(true);
    } else {
      setIsNightFlowAlert(false);
    }
  };

  useEffect(() => {
    setIsLoadRealTimeChart(false);
    getDataWeekly("base_flow");
    setIsNightFlowAlert(true);
    setIsLoadRealTimeChart(true);
  }, []);
  ///////////////////////////////////////////////

  useLayoutEffect(() => {
    import("./style.scss");
  }, []);

  const renderContent = () => {
    return isNilOrEmpty(dataWeekly) &&
      isNilOrEmpty(dataWeekly2) &&
      isNilOrEmpty(dataWeekly3) &&
      isNilOrEmpty(dataWeekly4) &&
      isNilOrEmpty(dataWeekly5) &&
      isNilOrEmpty(dataWeekly6) ? (
      <div className="no-item-message">
        {/* There is currently no usage data available. */}
      </div>
    ) : (
      <>
        {isNightFlowAlert && (
          <div className="card">
            <div className="card-body">
              {/* <Bar data={dataBarchart} options={options} height={100} /> */}

              <div className="row">
                {!isNilOrEmpty(dataWeekly) && (
                  <>
                    <div className="col-md-4">
                      <AlertCustomerRealTimeUsagePage
                        dataWeekly={dataWeekly}
                        meterNames={meterNames}
                        ticks={ticksRef.current}
                        customer={customer}
                        // aspect={aspect}
                      />
                      <h5 className="text-center">{nameCustomer}</h5>
                    </div>
                  </>
                )}
                {!isNilOrEmpty(dataWeekly2) && (
                  <div className="col-md-4">
                    <AlertCustomerRealTimeUsagePage
                      dataWeekly={dataWeekly2}
                      meterNames={meterNames2}
                      ticks={ticksRef.current}
                      customer={customer2}
                      // aspect={aspect}
                    />
                    <h5 className="text-center">{nameCustomer2}</h5>
                  </div>
                )}
                {!isNilOrEmpty(dataWeekly3) && (
                  <div className="col-md-4">
                    <AlertCustomerRealTimeUsagePage
                      dataWeekly={dataWeekly3}
                      meterNames={meterNames3}
                      ticks={ticksRef.current}
                      customer={customer3}
                      // aspect={aspect}
                    />
                    <h5 className="text-center">{nameCustomer3}</h5>
                  </div>
                )}
              </div>

              <div className="row">
                {!isNilOrEmpty(dataWeekly4) && (
                  <div className="col-md-4">
                    <AlertCustomerRealTimeUsagePage
                      dataWeekly={dataWeekly4}
                      meterNames={meterNames4}
                      ticks={ticksRef.current}
                      customer={customer4}
                      // aspect={aspect}
                    />
                    <h5 className="text-center">{nameCustomer4}</h5>
                  </div>
                )}
                {!isNilOrEmpty(dataWeekly5) && (
                  <div className="col-md-4">
                    <AlertCustomerRealTimeUsagePage
                      dataWeekly={dataWeekly5}
                      meterNames={meterNames5}
                      ticks={ticksRef.current}
                      customer={customer5}
                      // aspect={aspect}
                    />
                    <h5 className="text-center">{nameCustomer5}</h5>
                  </div>
                )}
                {!isNilOrEmpty(dataWeekly6) && (
                  <div className="col-md-4">
                    <AlertCustomerRealTimeUsagePage
                      dataWeekly={dataWeekly6}
                      meterNames={meterNames6}
                      ticks={ticksRef.current}
                      customer={customer6}
                      // aspect={aspect}
                    />
                    <h5 className="text-center">{nameCustomer6}</h5>
                  </div>
                )}
              </div>

              <div className="row">
                {!isNilOrEmpty(dataWeekly7) && (
                  <div className="col-md-4">
                    <AlertCustomerRealTimeUsagePage
                      dataWeekly={dataWeekly7}
                      meterNames={meterNames7}
                      ticks={ticksRef.current}
                      customer={customer7}
                      // aspect={aspect}
                    />
                    <h5 className="text-center">{nameCustomer7}</h5>
                  </div>
                )}
                {!isNilOrEmpty(dataWeekly8) && (
                  <div className="col-md-4">
                    <AlertCustomerRealTimeUsagePage
                      dataWeekly={dataWeekly8}
                      meterNames={meterNames8}
                      ticks={ticksRef.current}
                      customer={customer8}
                      // aspect={aspect}
                    />
                    <h5 className="text-center">{nameCustomer8}</h5>
                  </div>
                )}
                {!isNilOrEmpty(dataWeekly9) && (
                  <div className="col-md-4">
                    <AlertCustomerRealTimeUsagePage
                      dataWeekly={dataWeekly9}
                      meterNames={meterNames9}
                      ticks={ticksRef.current}
                      customer={customer9}
                      // aspect={aspect}
                    />
                    <h5 className="text-center">{nameCustomer9}</h5>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderNoContent = useCallback(() => {
    return onTabCustomerID
      ? "There are currently no alerts for this customer."
      : "There are currently no alerts.";
  }, [onTabCustomerID]);

  const renderContentBarChart = () => {
    return isNilOrEmpty(alertsChartData) ? (
      <div className="no-item-message">
        There is currently no usage data available.
      </div>
    ) : (
      <div className="card">
        <div className="card-body">
          <div style={{ width: "100%", height: "300px" }}>
            <Bar
              data={dataBarchart}
              options={{
                ...options,
                responsive: true,
                maintainAspectRatio: false,
              }}
            />
          </div>
          {!isNilOrEmpty(selectedPressureAlertSites) && (
            <div className="mt-4">
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    {(() => {
                      const severityLabel =
                        selectedPressureAlertSeverity === "low_1"
                          ? "Low 1"
                          : selectedPressureAlertSeverity === "low_2"
                          ? "Low 2"
                          : selectedPressureAlertSeverity === "high"
                          ? "High"
                          : selectedPressureAlertSeverity;
                      return (
                        <tr>
                            <th colSpan={3} className="text-center">Pressure alert sites on {selectedPressureAlertDate} {severityLabel && ` (${severityLabel})`}</th>
                        </tr>
                      );
                    })()}
                    <tr>
                      <th>Site ID</th>
                      <th>Meter ID</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPressureAlertSites.map((item, index) => {
                      return (
                        <tr key={`${item.customer_id || "na"}-${index}`}>
                          <td>{item.uuid || "-"}</td>
                          <td>{item.meter_id || "-"}</td>
                          <td>{item.address || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* <Bar data={dataBarchart} options={options} height={100} /> */}
        </div>
      </div>
    );
  };

  return userPermissions.length === 0 || !canViewThisPage ? null : (
    <div className="the-content">
      {isNil(onTabCustomerID) && <PageHeading title="Alerts" />}
      <div className="container-fluid content">
        <div className="action-buttons block-center-between">
          <div className="block-center">
            <AlertsFilter
              ref={filterRef}
              onFiltersApply={loadData}
              onTabCustomerID={onTabCustomerID}
            />
          </div>
          <div className="d-flex">
            {isLoaded && !tenantConfig.isDisabledTotalAlert && (
              <div className="alert-statistic">
                <span className="alert-statistic_high">
                  {displayDataOverview("high")(dataOverview)}
                </span>
                <span className="alert-statistic_medium">
                  {displayDataOverview("medium")(dataOverview)}
                </span>
                <span className="alert-statistic_low">
                  {displayDataOverview("low")(dataOverview)}
                </span>
              </div>
            )}
            <Button
              className="btn btn-default"
              href={exportUrl}
              disabled={!isLoaded}
            >
              Export
            </Button>
          </div>
        </div>

        {isNil(onTabCustomerID) && (
          <>
            {!isLoadedRealTimeChart ? (
              <AlertsPageChartRealTimeSkeleton />
            ) : hasAny.current ? (
              renderContent()
            ) : (
              renderNoContent()
            )}
          </>
        )}

        <AlertList
          ref={alertListRef}
          limit={limit}
          total={total}
          totalAll={totalAll}
          getSearchData={getSearchData(onTabCustomerID)}
          onTabCustomerID={onTabCustomerID}
          loadData={loadData}
          nightFlowAlert={nightFlowAlert}
        />

        {!isLoaded ? (
          <AlertsPageSkeleton numberOfItems={limit} />
        ) : (
          renderContentBarChart()
        )}

        {/* {!isLoaded ? (
          <AlertsPageSkeleton numberOfItems={limit} />
        ) : hasAny.current ? (
          renderContentBarChart()
        ) : (
          renderNoContent()
        )} */}
      </div>
    </div>
  );
};

export default AlertsPage;
